import React, { useEffect, forwardRef, useImperativeHandle, useRef, useState } from "react";
import styles from "./GlobalSearchResultsAutoSuggest.module.scss";
import { globalSearchIndexes, grad1, grad2, SITE_INDEX_HE, leumitSearchClient, } from "assets/constants";
import { getAllClassNames, getNameByKey, getPageByObjectType } from "helpers";
import {
  InstantSearch,
  Configure,
  useMenu,
  useSearchBox,
  UseSearchBoxProps
} from "react-instantsearch";
import strings from "assets/strings";
import { ResetButton } from "components/buttons/resetButton/ResetButton";
import { useAutocomplete } from "hooks";
import { HighlightText } from "components/highlightText/HighlightText";
//import { MenuItem } from "instantsearch.js/es/connectors/menu/connectMenu";
import SvgGenerator from "components/svgGenerator/SvgGenerator";


interface ExtendedSearchBoxProps extends UseSearchBoxProps {
  color1?: string;
  color2?: string;
  btnText?: string;
  label?: string;
  className?: string;
  onSubmit?: () => void;
  showMagnifier?: boolean;
}

const CustomSearchboxAutoSuggest = forwardRef((props: ExtendedSearchBoxProps, ref) => {

  const { query, refine } = useSearchBox(props);
  const { label, className, /*showMagnifier*/ } = props;
  const [selectedPill, setSelectedPill] = useState<string>('');
  const [localQuery, setLocalQuery] = useState(query); // New state for local input handling
  const results = useAutocomplete(localQuery, selectedPill ? `objectType:${selectedPill}` : '');
  const [inputValue, setInputValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const pillsRef = useRef<HTMLUListElement>(null);
  const suggestionListRef = useRef<HTMLUListElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  const handleSelectedPill = (pill: string) => {
    setSelectedPill(pill);
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      location.href = getPageByObjectType('all', true) + `?query=${localQuery}`;
    } else if (event.key === 'ArrowDown' && suggestionListRef.current) {
      event.preventDefault();
      const firstItem = suggestionListRef.current.querySelector('li');
      if (firstItem) {
        firstItem.focus();
        // Ensure the first item is visible without scrolling
        firstItem.scrollIntoView({ block: 'nearest' });
      }
    } else if (event.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  }

  const handleSuggestionKeyDown = (event: React.KeyboardEvent, index: number, hit: any) => {
    const items = suggestionListRef.current?.querySelectorAll('li');
    if (!items) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextItem = items[index + 1] || items[0];
      nextItem.focus();
      nextItem.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevItem = items[index - 1] || items[items.length - 1];
      prevItem.focus();
      prevItem.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onNavigate(hit);
    }
  };

  const handleReset = () => {
    setLocalQuery(''); // Clear the local input
    setInputValue('');
    refine(inputValue);
  };

  function setQuery(newQuery: string) {
    setLocalQuery(newQuery);
    setInputValue(newQuery);
    refine(newQuery);
  }

  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(ref, () => ({
    invokeChildFunction: setQuery
  }));

  const onNavigate = (hit: any) => {
    if (!hit.url) return;
    else {
      const { url } = hit;
      const pageUrl = hit.hasOwnProperty('hasMultiple')
        ? getPageByObjectType(hit.objectType, hit.hasMultiple)
        : getPageByObjectType(hit.objectType);

      window.open(pageUrl + url, "_self");
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[A-Za-zא-ת0-9()\s\-]+$/;    
    if (event.currentTarget.value !== '' && !regex.test(event.currentTarget.value)) return;
    const value = event.currentTarget.value;
    setLocalQuery(value);
    if (value.length > 2) {
      setIsDropdownOpen(true);
      refine(value);
    } else {
      setIsDropdownOpen(false);
      refine('');
    }
  };

  return (
    <div
      className={`${styles.container} search-field`}
      ref={containerRef}
    >
      <form
        className={styles.searchboxWrapper}
        action=""
        role="search"
        noValidate
        ref={formRef}
        onReset={() => handleReset()}
      >
        <div className={getAllClassNames([styles.inputWrapper, className ? className : ''])}>{label && <label htmlFor='searchbox' className={styles.text}>{label}</label>}
          <input
            autoComplete="off"
            placeholder={strings.globalSearchResults.allSearchResults}
            role="combobox" aria-autocomplete="list" aria-expanded={`${results.hits.length ? 'true' : 'false'}`} aria-controls="cb1-listbox"
            className={getAllClassNames(['ais-SearchBox-input', styles.input])}
            ref={inputRef}
            type="search"
            value={localQuery}
            //autoFocus
            id='searchbox'
            onChange={handleInputChange}
            onKeyDown={handleSearchKeyDown}
          />
          {query ? <ResetButton
            onReset={() => handleReset()}
          /> : <SvgGenerator className={styles.magSvg} name="Search" objectName="zakautSearchResults" />
          }

          <div id="cb1-listbox" className={`${styles.collapseSuggest} collapse ${isDropdownOpen && results.hits.length ? 'show' : ''}`}>
            {localQuery.length > 2 && <Pills onSelectedPill={(pill: string) => handleSelectedPill(pill)} ref={pillsRef} suggestionListRef={suggestionListRef} />}
            <ul role="listbox" className={styles.collapseSuggestList} aria-live="polite" ref={suggestionListRef}>
              {results.hits.map((hit, index) => (
                <li role="option" key={hit.objectID} onClick={() => onNavigate(hit)} tabIndex={0} onKeyDown={(event) => handleSuggestionKeyDown(event, index, hit)} className={styles.collapseSuggestItem}>
                  <HighlightText query={query} text={hit.title} />
                  {/* {hit.url ?
                    <a href={`${getPageByObjectType(hit.objectType) ? getPageByObjectType(hit.objectType) : ''}${hit.url}`}><HighlightText query={query} text={hit.title} /></a> : <HighlightText query={query} text={hit.title} />
                  } */}
                </li>
              ))}
            </ul>
            {results.hits.length > 49 && <div className={styles.showAll} ><a className={styles.btnText} href={getPageByObjectType('all', true) + `?query=${query}`} >{strings.globalStrings.moreResults}</a></div>}
          </div>
        </div>
      </form>
    </div>
  );
});

interface PillsProps {
  onSelectedPill: (refiner: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  suggestionListRef: React.RefObject<HTMLUListElement>;
}

const Pills = forwardRef(({ onSelectedPill, suggestionListRef }: PillsProps, ref: React.ForwardedRef<any>) => {
  const { items, refine } = useMenu({ attribute: 'objectType', limit: 15, sortBy: ['count:desc'] });
  const [totalHits, setTotalHits] = React.useState<number>(0);
  const [currentPill, setCurrentPill] = React.useState<string>('');

  useImperativeHandle(ref, () => ({
    handleSelectedPill,
  }));

  useEffect(() => {
    if (!items || items.length === 0) return;
  
    const clonedItems = [...items];
    const pageIndex = clonedItems.findIndex(obj => obj.value === 'Page');
    if (pageIndex !== -1) {
      const [pageItem] = clonedItems.splice(pageIndex, 1);
      clonedItems.push(pageItem);
    }
  
    const total = clonedItems.reduce((acc, item) => acc + item.count, 0);
    setTotalHits(total);
  }, [items]);


  const handleSelectedPill = (refiner: string) => {
    onSelectedPill(refiner);
    refine(refiner);
    setCurrentPill(refiner);
  };

  const handlePillsKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && suggestionListRef.current) {
      suggestionListRef.current.querySelector('li')?.focus();
    }
  };

  return (
    totalHits ? <div className={styles.searchpills}>
      <ul ref={ref}>
        <li>
          <button
            onClick={() => currentPill === '' ? false : handleSelectedPill('')}
            className={currentPill === '' ? styles.active : ""}
            type="button"
            onKeyDown={handlePillsKeyDown}
          >
            {`${strings.globalSearchResults.allResults} (${totalHits})`}
          </button>
        </li>

        {items && items.map((refiner) => {
          return (
            <li key={refiner?.label}>
              <button
                onClick={() => refiner?.isRefined ? false : handleSelectedPill(refiner?.label)}
                className={refiner?.isRefined ? styles.active : ""}
                type="button"
                onKeyDown={handlePillsKeyDown}
              >
                {`${getNameByKey(refiner?.label, globalSearchIndexes)} (${refiner?.count})`}
              </button>
            </li>
          )
        }
        )}
      </ul>
    </div> : null
  )
});

export const GlobalSearchResultsAutoSuggest = () => {

  return (
    <InstantSearch
      indexName={SITE_INDEX_HE}
      searchClient={leumitSearchClient}

    >
      {<Configure />}
      <CustomSearchboxAutoSuggest color1={grad1} color2={grad2} btnText={strings.globalStrings.search} className={styles.globalSearch} />
      {/* <VirtualQuery /> */}
    </InstantSearch>
  );
}
